import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { useApp } from '../context/AppContext';
import WeaknessRadar from './WeaknessRadar';
import StoryChapter from './StoryChapter';

const PULSE_AVATAR = 'https://customer-assets-jt897jd0.emergentagent.net/job_63727204-8e2f-45b7-8117-c2998f36774d/artifacts/aaq1h17c_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2009_58_40%20PM.png';
const PIXIE_AVATAR = 'https://customer-assets-jt897jd0.emergentagent.net/job_63727204-8e2f-45b7-8117-c2998f36774d/artifacts/3ofipriz_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2010_52_00%20PM.png';

export default function ChatBot({ bot = 'pulse' }) {
  const { API } = useApp();
  const isPixie = bot === 'pixie';
  const [open, setOpen] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const sessionIdRef = useRef(`sess_${Date.now()}_${bot}`);
  const scrollRef = useRef(null);

  // Allow other UI (e.g., capability card) to open the chatbot
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('pp:open-chatbot', handler);
    return () => window.removeEventListener('pp:open-chatbot', handler);
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      const greeting = isPixie
        ? "Hello, dear scholar! I'm Pixie 🧚 — sister to Pulse. Ask me anything about your ACT quest!"
        : "// Pulse online. I'm your ACT tutor. Give me a topic, a question, or ask for a strategy.";
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      const r = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionIdRef.current, bot }),
      });
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, '').trim();
          if (!line) continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) {
              setMessages(m => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + j.delta };
                return c;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', content: `Connection error: ${e.message}` }]);
    } finally { setStreaming(false); }
  };

  const avatar = isPixie ? PIXIE_AVATAR : PULSE_AVATAR;
  const botName = isPixie ? 'Pixie' : 'Pulse';

  return (
    <>
      {/* Character-as-button — hidden while chat is open */}
      {!open && (
        <div
          className="fixed bottom-4 right-4 z-40 flex flex-col items-end"
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
        >
          <AnimatePresence>
            {hovering && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.9 }}
                data-testid={`chatbot-hover-tooltip-${bot}`}
                className={`mb-2 mr-2 px-4 py-2 rounded-2xl text-sm whitespace-nowrap relative ${
                  isPixie
                    ? 'bg-[#FDF7E6] border-2 border-[#8C6B3D] text-[#2C2621] font-storybook shadow-lg'
                    : 'bg-black border border-[#00FFD1] text-[#00FFD1] font-mono-cyber cyber-glow'
                }`}
              >
                Ask me your questions!
                <span
                  className="absolute -bottom-1.5 right-8 w-3 h-3 rotate-45"
                  style={{
                    background: isPixie ? '#FDF7E6' : '#000',
                    borderRight: isPixie ? '2px solid #8C6B3D' : '1px solid #00FFD1',
                    borderBottom: isPixie ? '2px solid #8C6B3D' : '1px solid #00FFD1',
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            data-testid={`chatbot-toggle-${bot}`}
            onClick={() => setOpen(true)}
            whileHover={{ scale: 1.06, y: -4 }}
            whileTap={{ scale: 0.96 }}
            className="w-24 h-24 sm:w-28 sm:h-28 p-0 bg-transparent border-0 cursor-pointer"
            style={{ filter: isPixie
              ? 'drop-shadow(0 6px 18px rgba(232,180,200,0.6))'
              : 'drop-shadow(0 6px 18px rgba(0,255,209,0.55))' }}
          >
            <img src={avatar} alt={botName} className="w-full h-full object-contain" />
          </motion.button>
        </div>
      )}

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            className={`fixed bottom-6 right-6 z-40 w-[380px] max-w-[92vw] h-[520px] rounded-2xl flex flex-col ${
              isPixie ? 'parchment' : 'glass-cyber cyber-glow'
            }`}
            style={{ overflow: 'visible' }}
          >
            {/* Floating character on top-left of chat */}
            <img
              src={avatar}
              alt={botName}
              data-testid={`chatbot-float-char-${bot}`}
              className="char-floats absolute -top-16 -left-6 w-28 h-28 object-contain pointer-events-none"
            />

            <div className={`flex items-center justify-between px-4 py-3 rounded-t-2xl ${
              isPixie ? 'border-b-2 border-[#C9B99A]' : 'border-b border-[#00FFD1]/30'
            }`}>
              <div className="pl-24">
                <div className={isPixie ? 'font-storybook text-lg text-[#2C2621]' : 'font-orbitron tracking-widest text-[#00FFD1]'}>
                  {botName}
                </div>
                <div className={`text-[10px] ${isPixie ? 'text-[#68615A] italic' : 'font-mono-cyber text-neutral-400'}`}>
                  {isPixie ? 'your fairy tutor' : '// online'}
                </div>
              </div>
              <button data-testid={`chatbot-close-${bot}`} onClick={() => setOpen(false)}
                className={isPixie ? 'text-[#68615A]' : 'text-[#00FFD1]'}>
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto pp-scroll px-4 py-3 space-y-3">
              {/* Briefing: WeaknessRadar for Pulse, StoryChapter for Pixie */}
              <div data-testid={`chatbot-briefing-${bot}`} className={`rounded-xl p-3 ${
                isPixie ? '' : 'border border-[#00FFD1]/30 bg-black/50'
              }`}>
                {isPixie ? <StoryChapter /> : <WeaknessRadar variant="cyber" />}
              </div>

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                    m.role === 'user'
                      ? (isPixie ? 'bg-[#2B5329] text-white' : 'bg-[#00FFD1] text-black font-medium')
                      : (isPixie ? 'bg-[#FDFBF7] border border-[#C9B99A] text-[#2C2621]' : 'bg-black/70 border border-[#00FFD1]/30 text-neutral-100')
                  }`}>
                    <span className={isPixie ? 'font-body-story' : 'font-mono-cyber'}>
                      {m.content || (streaming && i === messages.length - 1 ? '…' : '')}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className={`p-3 flex gap-2 rounded-b-2xl ${
              isPixie ? 'border-t-2 border-[#C9B99A] bg-[#F4EFE6]' : 'border-t border-[#00FFD1]/30 bg-black/60'
            }`}>
              <input
                data-testid={`chatbot-input-${bot}`}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder={isPixie ? 'Ask Pixie a question…' : '> query pulse…'}
                className={`flex-1 px-3 py-2 rounded-lg text-sm outline-none ${
                  isPixie
                    ? 'bg-white border border-[#C9B99A] text-[#2C2621] font-body-story placeholder-[#8C6B3D]'
                    : 'bg-black border border-[#00FFD1]/40 text-[#00FFD1] font-mono-cyber placeholder-[#00FFD1]/40'
                }`}
              />
              <button data-testid={`chatbot-send-${bot}`} onClick={send} disabled={streaming}
                className={`px-3 rounded-lg ${isPixie ? 'bg-[#2B5329] text-white' : 'bg-[#00FFD1] text-black'}`}>
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
